import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute, AdminRoute, ReviewerRoute, SchoolLeaderRoute } from './components/ProtectedRoute';
import { initMetaPixel, trackPageView } from './metaPixel';

import './styles/global.css';

// Pages
import Login          from './pages/Login';
import Register       from './pages/Register';
import Dashboard      from './pages/Dashboard';
import ExamPage       from './pages/Exam';
import Level          from './pages/Level';
import BillingSuccess from './pages/BillingSuccess';
import Pricing        from './pages/Pricing';
import Schools        from './pages/Schools';
import AdminPanel     from './pages/AdminPanel';
import Review         from './pages/Review';
import Home           from './pages/Home';
import TeacherDashboard from './pages/TeacherDashboard';
import MockExam       from './pages/MockExam';   // Chunk 52

// Global components
import WhatsAppFloat  from './components/WhatsAppFloat';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Initialises the Meta Pixel once, then fires a PageView on every route change.
// Dormant until REACT_APP_META_PIXEL_ID is set (see metaPixel.js), so it is a
// safe no-op until the Pixel ID is configured in Vercel.
function MetaPixelTracker() {
  const { pathname } = useLocation();
  useEffect(() => { initMetaPixel(); }, []);
  useEffect(() => { trackPageView(); }, [pathname]);
  return null;
}

// Syncs <html dir> and <html lang> whenever the language changes.
// Lives inside BrowserRouter so it re-renders correctly.
function LanguageSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const isAr = i18n.language === 'ar';
    document.documentElement.dir  = isAr ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageSync />
        <ScrollToTop />
        <MetaPixelTracker />
        <Routes>
          {/* Public */}
          <Route path="/"         element={<Home />} />
          <Route path="/pricing"  element={<Pricing />} />
          <Route path="/schools"  element={<Schools />} />
          <Route path="/login"    element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Protected — any authenticated user */}
          <Route path="/dashboard" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/exam/:exam" element={
            <ProtectedRoute><ExamPage /></ProtectedRoute>
          } />
          <Route path="/exam/:exam/world/:worldKey/level/:levelNumber" element={
            <ProtectedRoute><Level /></ProtectedRoute>
          } />
          <Route path="/billing/success" element={
            <ProtectedRoute><BillingSuccess /></ProtectedRoute>
          } />
          {/* Chunk 52: mock exam sitting screen (backend enforces org/class/window) */}
          <Route path="/mock/:assignmentId" element={
            <ProtectedRoute><MockExam /></ProtectedRoute>
          } />

          {/* Admin only */}
          <Route path="/admin" element={
            <AdminRoute><AdminPanel /></AdminRoute>
          } />

          {/* Reviewer only */}
          <Route path="/review" element={
            <ReviewerRoute><Review /></ReviewerRoute>
          } />

          {/* School leader (own org) or admin (any org) — D4 */}
          <Route path="/school" element={
            <SchoolLeaderRoute><TeacherDashboard /></SchoolLeaderRoute>
          } />
          <Route path="/school/:orgId" element={
            <SchoolLeaderRoute><TeacherDashboard /></SchoolLeaderRoute>
          } />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* WhatsApp float visible on every page */}
        <WhatsAppFloat />
      </AuthProvider>
    </BrowserRouter>
  );
}